import { useQuery } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/hooks/state';
import { RoleName, getUserIdFromJWT } from '@/services/auth';
import apiClient from '@/lib/apiClient';
import { STALE } from '@/lib/queryPolicy';
import { queryKeys } from '@/lib/queryKeys';

interface WorkspaceAccess {
  account_id: string | null;
  roles: RoleName[];
  is_superadmin: boolean;
}

/** One caller-scoped read, not workspace → full account roster. This is only
 * a UI hint; backend guards authorize every protected action independently. */
export function useUserRole() {
  const { workspaceId, isSuperadmin } = useWorkspaceStore();
  const userId = getUserIdFromJWT();

  const query = useQuery<WorkspaceAccess>({
    queryKey: queryKeys.workspaceAccess.detail(workspaceId, userId),
    queryFn: () => apiClient.get(`/workspaces/${workspaceId}/my-access`),
    enabled: !!workspaceId && !!userId,
    staleTime: STALE.stable,
    retry: false,
  });

  const access = query.isError ? undefined : query.data;
  const accountId = access?.account_id ?? null;
  const roles = access?.roles ?? [];

  const hasRole = (role: RoleName): boolean => roles.includes(role);

  const isAdmin = roles.includes('ADMIN') || (access?.is_superadmin ?? isSuperadmin);

  const isLoading = !!workspaceId && !!userId && query.isLoading;

  return {
    roles,
    accountId,
    isLoading,
    hasRole,
    isAdmin,
    userId,
  };
}
